import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import React, { useMemo } from 'react';

import { LinkedShipmentRow } from './LinkedShipmentRow';
import { LinkedShipmentsPanelProps } from './LinkedShipmentsPanel.props';
import { PartHistoryDataType } from '../../../../../@types/part.type';
import { Panel } from '../../../../../components/atoms/Panel';

const headerSx = {
  fontSize: '11px',
  py: 0.75,
  px: 1.5,
  fontWeight: 600,
  color: 'text.secondary',
  textTransform: 'uppercase' as const,
  letterSpacing: '0.03em',
};

/** Use partHistory just for the unique shipmentIds — live status comes from useGetOneShipment per row. */
const uniqueShipmentIds = (history: PartHistoryDataType[]): number[] => {
  const seen = new Map<number, string>();
  for (const entry of history) {
    if (!entry.shipmentId) continue;
    const existing = seen.get(entry.shipmentId);
    if (!existing || (entry.createdAt ?? '') > existing) {
      seen.set(entry.shipmentId, entry.createdAt ?? '');
    }
  }
  return Array.from(seen.entries())
    .sort((a, b) => b[1].localeCompare(a[1]))
    .map(([id]) => id);
};

export const LinkedShipmentsPanel: React.FC<LinkedShipmentsPanelProps> = ({ partHistory, sx }) => {
  const ids = useMemo(() => uniqueShipmentIds(partHistory), [partHistory]);

  return (
    <Panel title={`Linked Shipments${ids.length ? ` · ${ids.length}` : ''}`} sx={sx}>
      {ids.length === 0 ? (
        <Typography
          sx={{ fontSize: '13px', color: 'text.secondary', py: 1.5, textAlign: 'center' }}
        >
          No shipments linked to this part
        </Typography>
      ) : (
        <TableContainer>
          <Table size="small" aria-label="Linked shipments">
            <TableHead>
              <TableRow>
                <TableCell sx={headerSx}>Shipment ID</TableCell>
                <TableCell sx={headerSx}>Status</TableCell>
                <TableCell sx={headerSx}>Type</TableCell>
                <TableCell sx={headerSx}>Route</TableCell>
                <TableCell sx={headerSx}>Last Updated</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {ids.map((id, idx) => (
                <LinkedShipmentRow key={id} shipmentId={id} zebra={idx % 2 === 1} />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Panel>
  );
};
